import i18next from 'i18next';
import React, { useCallback, useMemo } from 'react';

import { AgencyOrgIcon, CustomerOrgIcon, WhiteLabelOrgIcon } from '~/assets/organization';
import { Organization, OrganizationPageQueryOrganization, User } from '~/constants/graphqlTypes';
import {
  Organization as FullOrganization,
  OrganizationType,
  Role,
  useListOrganizationRolesQuery,
} from '~/types/api.graphql';

import { getRoleTitle } from './User';

export const mapOrgTypeIcons = {
  [OrganizationType.WhiteLabel]: <WhiteLabelOrgIcon />,
  [OrganizationType.Agency]: <AgencyOrgIcon />,
  [OrganizationType.Customer]: <CustomerOrgIcon />,
};

export const getOrganizationInitials = (organizationName: string) => {
  const words = organizationName.split(' ');
  const firstWord = words[0];
  const secondWord = words[1];
  const firstLetter = firstWord[0] || '';
  const secondLetter = secondWord ? secondWord[0] : firstWord[1] || '';
  return `${firstLetter}${secondLetter}`.toUpperCase();
};

export interface OrganizationAddress {
  addressLine1?: string;
  address?: string;
  city?: string;
  state?: string;
}

export type ExtendedOrganization = FullOrganization & { __typename?: string };

export const getOrganizationAddress = (organization: OrganizationAddress) => {
  return [organization?.addressLine1 || organization?.address, organization?.city, organization?.state]
    .map((component) => component?.trim())
    .filter((component) => !!component)
    .join(', ');
};

export const validateOrgPlan = async (organization: Organization, getSubscriptionInfo) => {
  if (organization?.subscriptionOverride) return true;
  if (!organization?.paidSubscription) return false;
  try {
    const result = await getSubscriptionInfo();
    return !!result?.data?.subscriptionInfo;
  } catch (error) {
    return false;
  }
};

export const isB2BOrg = (organization: Organization) => organization?.businessDataEnabled;
export const isFreeOrg = (organization: Organization) =>
  !(organization.paidSubscription || organization.subscriptionOverride);

export const isEmailValidationEnabled = (
  organization: Pick<OrganizationPageQueryOrganization, 'disableEmailValidationOwn' | 'disableEmailValidationFinal'>,
): boolean => {
  if (!organization) return true; // Default to enabled if org is not available

  // Get the organization's own value (retrieveFinalValue: false)
  const { disableEmailValidationOwn } = organization;

  // If the org has its own value (not null), use it
  if (typeof disableEmailValidationOwn === 'boolean') {
    return !disableEmailValidationOwn;
  }
  // If it's null, it means inheriting from parent, so use the final value (retrieveFinalValue: true)
  const { disableEmailValidationFinal } = organization;
  return !disableEmailValidationFinal;
};

export const getOrganizationRole = (organization: OrganizationPageQueryOrganization, me: User) => {
  const userRole = me?.userRoles?.find((role) => role?.organizationId === organization?.id);
  if (!userRole) return i18next.t('organizations.notMember');
  return getRoleTitle(userRole?.assignedRole?.name);
};

export const useRoles = (orgId: string) => {
  const { data, loading } = useListOrganizationRolesQuery({
    variables: {
      organizationId: orgId,
    },
    skip: !orgId,
  });

  const isRoleDuplicated = useCallback(
    (role: Role) => data?.listOrganizationRoles?.some((r) => r.name === role.name && r.id !== role.id),
    [data],
  );

  const roles = useMemo(
    () =>
      data?.listOrganizationRoles?.map((role) => ({
        ...role,
        name: isRoleDuplicated(role as Role) ? `${role.name} (${role.organization?.name})` : role.name,
      })) || [],
    [data],
  );

  return { roles, loading };
};
